// /opt/isp_erp/frontend/src/pages/tickets/CalendarView.jsx
import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import es from 'date-fns/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert
} from '@mui/material';
import { scheduleAPI } from '../../api/schedule.api';
import api from '../../api/axios';

const locales = { 'es': es };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

// Leyenda de colores
const Legend = () => (
  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2 }}>
    <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 1 }}>📋 Tipos:</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 16, height: 16, bgcolor: '#4caf50', borderRadius: 1 }} />
      <Typography variant="caption">Instalación</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 16, height: 16, bgcolor: '#ff9800', borderRadius: 1 }} />
      <Typography variant="caption">Retiro</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 16, height: 16, bgcolor: '#2196f3', borderRadius: 1 }} />
      <Typography variant="caption">Cambio Equipo</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 16, height: 16, bgcolor: '#f44336', borderRadius: 1 }} />
      <Typography variant="caption">Incidente</Typography>
    </Box>
    <Typography variant="caption" sx={{ fontWeight: 'bold', ml: 2, mr: 1 }}>⚠️ Estados:</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 16, height: 16, bgcolor: '#4caf50', borderRadius: 1 }} />
      <Typography variant="caption">Programado</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 16, height: 16, bgcolor: '#ff9800', borderRadius: 1 }} />
      <Typography variant="caption">En Progreso</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 16, height: 16, bgcolor: '#757575', borderRadius: 1 }} />
      <Typography variant="caption">Completado/Cerrado</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 16, height: 16, bgcolor: '#b71c1c', borderRadius: 1, border: '2px solid #ff0000' }} />
      <Typography variant="caption" color="error">⚠️ VENCIDO (Atención urgente)</Typography>
    </Box>
  </Box>
);

const CalendarView = () => {
  const [events, setEvents] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadTechnicians();
  }, []);

  useEffect(() => {
    if (selectedTechnician) {
      loadSchedule();
    }
  }, [selectedTechnician]);

  const loadTechnicians = async () => {
    try {
      const response = await api.get('/users');
      const data = response.data || [];
      setTechnicians(data);
      if (data && data.length > 0) {
        // Seleccionar el primer técnico disponible
        setSelectedTechnician(data[0].id);
      }
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  const loadSchedule = async () => {
    if (!selectedTechnician) return;

    setLoading(true);
    try {
      // Mostrar desde 3 meses atrás hasta 6 meses adelante
      const start = new Date();
      start.setMonth(start.getMonth() - 3);
      start.setHours(0, 0, 0, 0);
      
      const end = new Date();
      end.setMonth(end.getMonth() + 6);
      end.setHours(23, 59, 59, 999);

      const data = await scheduleAPI.getTechnicianSchedule(selectedTechnician, start, end);

      const formattedEvents = data.map(event => ({
        id: event.id,
        title: `${event.ticket_type?.toUpperCase() || ''} - ${event.ticket_number || 'N/A'}`,
        start: new Date(event.scheduled_date),
        end: new Date(new Date(event.scheduled_date).getTime() + event.duration_minutes * 60 * 1000),
        type: event.ticket_type,
        status: event.status,
        technician_name: event.technician_name,
        customer_name: event.customer_name,
        notes: event.notes
      }));

      setEvents(formattedEvents);
    } catch (error) {
      console.error('Error loading schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para determinar si un evento está vencido
  const isEventOverdue = (event) => {
    const now = new Date();
    const eventDate = new Date(event.start);
    // Vencido si la fecha ya pasó y no está completado/cerrado/cancelado
    const notCompleted = event.status !== 'completado' && event.status !== 'cerrado' && event.status !== 'cancelado';
    return eventDate < now && notCompleted;
  };

  const eventStyleGetter = (event) => {
    const now = new Date();
    const eventDate = new Date(event.start);
    const isOverdue = eventDate < now && event.status !== 'completado' && event.status !== 'cerrado' && event.status !== 'cancelado';
    
    // Si está vencido, mostrar en ROJO intenso con borde
    if (isOverdue) {
      return {
        style: {
          backgroundColor: '#b71c1c',
          borderColor: '#ff0000',
          color: 'white',
          borderRadius: '6px',
          border: '2px solid #ff0000',
          padding: '4px 8px',
          fontSize: '12px',
          fontWeight: 'bold',
          cursor: 'pointer',
          boxShadow: '0 0 5px rgba(255,0,0,0.5)'
        }
      };
    }
    
    // Colores por tipo de ticket (si no está vencido)
    let backgroundColor = '#9e9e9e';
    switch (event.type) {
      case 'instalacion': backgroundColor = '#4caf50'; break;
      case 'retiro': backgroundColor = '#ff9800'; break;
      case 'cambio_equipo': backgroundColor = '#2196f3'; break;
      case 'incidente': backgroundColor = '#f44336'; break;
      default: backgroundColor = '#9e9e9e';
    }
    
    // Si está completado o cerrado, reducir opacidad
    let opacity = 1;
    if (event.status === 'completado' || event.status === 'cerrado') {
      opacity = 0.7;
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        color: 'white',
        border: 'none',
        padding: '4px 8px',
        fontSize: '12px',
        fontWeight: 500,
        cursor: 'pointer',
        opacity,
        transition: 'all 0.2s ease'
      }
    };
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const getEventTypeLabel = (type) => {
    switch (type) {
      case 'instalacion': return '📦 Instalación';
      case 'retiro': return '📤 Retiro';
      case 'cambio_equipo': return '🔄 Cambio de Equipo';
      case 'incidente': return '⚠️ Incidente';
      default: return type;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'programado': return '✅ Programado';
      case 'en_progreso': return '🔄 En Progreso';
      case 'completado': return '✔️ Completado';
      case 'cerrado': return '🔒 Cerrado';
      case 'cancelado': return '❌ Cancelado';
      default: return status || 'Desconocido';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'programado': return 'success';
      case 'en_progreso': return 'warning';
      case 'completado': return 'info';
      case 'cerrado': return 'secondary';
      case 'cancelado': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Calendario de Trabajos
      </Typography>

      {/* Leyenda de colores */}
      <Legend />

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Técnico</InputLabel>
            <Select
              value={selectedTechnician}
              onChange={(e) => setSelectedTechnician(e.target.value)}
              label="Técnico"
            >
              {technicians.map(tech => (
                <MenuItem key={tech.id} value={tech.id}>
                  {tech.username || tech.nombre || tech.email} - {tech.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip label="📦 Instalación" sx={{ bgcolor: '#4caf50', color: 'white' }} size="small" />
            <Chip label="📤 Retiro" sx={{ bgcolor: '#ff9800', color: 'white' }} size="small" />
            <Chip label="🔄 Cambio" sx={{ bgcolor: '#2196f3', color: 'white' }} size="small" />
            <Chip label="⚠️ Incidente" sx={{ bgcolor: '#f44336', color: 'white' }} size="small" />
            <Chip label="🔴 VENCIDO" sx={{ bgcolor: '#b71c1c', color: 'white', border: '1px solid #ff0000' }} size="small" />
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ height: '70vh' }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            views={['month', 'week', 'day', 'agenda']}
            defaultView="week"
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleSelectEvent}
            messages={{
              next: "Siguiente",
              previous: "Anterior",
              today: "Hoy",
              month: "Mes",
              week: "Semana",
              day: "Día",
              agenda: "Agenda"
            }}
            culture="es"
          />
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          bgcolor: selectedEvent && isEventOverdue(selectedEvent) ? '#b71c1c' :
                    selectedEvent?.type === 'incidente' ? '#f44336' :
                    selectedEvent?.type === 'instalacion' ? '#4caf50' :
                    selectedEvent?.type === 'retiro' ? '#ff9800' : '#2196f3',
          color: 'white'
        }}>
          Detalle del Trabajo
          {selectedEvent && isEventOverdue(selectedEvent) && (
            <Chip label="⚠️ VENCIDO - ATENCIÓN URGENTE" color="error" size="small" sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {getEventTypeLabel(selectedEvent.type)} - {selectedEvent.title}
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>Cliente:</strong> {selectedEvent.customer_name || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Técnico:</strong> {selectedEvent.technician_name || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Fecha:</strong> {format(selectedEvent.start, 'dd/MM/yyyy')}
              </Typography>
              <Typography variant="body2">
                <strong>Hora:</strong> {format(selectedEvent.start, 'HH:mm')} - {format(selectedEvent.end, 'HH:mm')}
              </Typography>
              <Typography variant="body2">
                <strong>Duración:</strong> {Math.round((selectedEvent.end - selectedEvent.start) / (1000 * 60))} minutos
              </Typography>
              {selectedEvent.notes && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Notas:</strong> {selectedEvent.notes}
                </Typography>
              )}
              <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip
                  label={getStatusLabel(selectedEvent.status)}
                  size="small"
                  color={getStatusColor(selectedEvent.status)}
                />
                {isEventOverdue(selectedEvent) && (
                  <Alert severity="error" sx={{ mt: 1, width: '100%' }}>
                    ⚠️ Este trabajo está VENCIDO. La fecha programada ya pasó y no se ha completado.
                  </Alert>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarView;
